import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Users, ChevronLeft } from "lucide-react";
import { myJobListings, listingApplications, changeApplicationStatus } from "@/lib/jobsApi";
import ErrorState from "@/pages/jobs/ErrorState";

const NEXT_STATUSES = ["viewed", "shortlisted", "interviewing", "rejected", "hired"];

export default function JobEmployerPage() {
  const [sp, setSp] = useSearchParams();
  const listingId = sp.get("listing");
  return listingId ? <ApplicantsView listingId={listingId} onBack={() => setSp({})} /> : <ListingsList onOpen={(id) => setSp({ listing: id })} />;
}

function ListingsList({ onOpen }) {
  const q = useQuery({ queryKey: ["my-job-listings"], queryFn: myJobListings });
  const rows = q.data?.listings || [];
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <h1 className="font-display text-3xl font-black text-slate-900" data-testid="employer-title">Employer dashboard</h1>
      <p className="text-sm text-slate-500 mt-1">Your job listings and applicants.</p>
      <ul className="mt-6 space-y-3" data-testid="employer-listings">
        {q.isLoading && <li className="text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</li>}
        {q.isError && <li><ErrorState onRetry={() => q.refetch()} /></li>}
        {!q.isLoading && !q.isError && !rows.length && <li className="text-slate-500 text-sm">No job listings yet.</li>}
        {rows.map((l) => (
          <li key={l.id} data-testid={`emp-listing-${l.id}`} className="rounded-2xl bg-white border border-soft p-4 md:p-5 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <button onClick={() => onOpen(l.id)} className="font-display font-bold text-slate-900 hover:text-[color:var(--brand-blue)] text-left" data-testid={`emp-open-${l.id}`}>{l.title}</button>
              <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {l.applicants_total} applicants{l.applicants_new > 0 && ` · ${l.applicants_new} new`}</span>
                {l.high_volume && <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase">High volume</span>}
                <span>{l.questions_count} question{l.questions_count === 1 ? "" : "s"}</span>
              </div>
            </div>
            <Link to={`/ad/${l.id}`} className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline">View listing</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ApplicantsView({ listingId, onBack }) {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("recent");
  const q = useQuery({
    queryKey: ["listing-applications", listingId, statusFilter, sort],
    queryFn: () => listingApplications(listingId, { status: statusFilter || undefined, sort }),
  });
  const [selected, setSelected] = useState(new Set());
  const statusMut = useMutation({
    mutationFn: (payload) => changeApplicationStatus(payload),
    onSuccess: () => { toast.success("Status updated."); setSelected(new Set()); qc.invalidateQueries({ queryKey: ["listing-applications"] }); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Status update failed"),
  });
  const rows = q.data?.applications || [];
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkSet = (status) => statusMut.mutate({ ids: [...selected], status });
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-[color:var(--brand-blue)] inline-flex items-center gap-1" data-testid="applicants-back">
        <ChevronLeft className="w-4 h-4" /> All listings
      </button>
      <h1 className="mt-2 font-display text-2xl md:text-3xl font-black text-slate-900" data-testid="applicants-title">Applicants</h1>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select data-testid="applicants-status-filter" className="input w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {["submitted","viewed","shortlisted","interviewing","rejected","hired"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="applicants-sort" className="input w-auto" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="recent">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
        {selected.size > 0 && (
          <div className="ml-auto flex flex-wrap items-center gap-2" data-testid="applicants-bulk-bar">
            <span className="text-sm text-slate-500">{selected.size} selected</span>
            {NEXT_STATUSES.map((s) => (
              <button key={s} data-testid={`bulk-${s}`} onClick={() => bulkSet(s)} disabled={statusMut.isPending} className="text-xs font-semibold rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-3 py-1.5">
                Set {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <ul className="mt-6 space-y-3" data-testid="applicants-list">
        {q.isLoading && <li className="text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</li>}
        {q.isError && <li><ErrorState onRetry={() => q.refetch()} /></li>}
        {!q.isLoading && !q.isError && !rows.length && <li className="text-slate-500 text-sm">No applicants match.</li>}
        {rows.map((a) => {
          // Real /listings/{id}/applications payload uses `applicant`;
          // keep `snapshot` fallback to survive future normalisation.
          const s = a.applicant || a.snapshot || {};
          return (
          <li key={a.id} data-testid={`applicant-${a.id}`} className="rounded-2xl bg-white border border-soft p-4 md:p-5 flex items-center gap-4">
            <input type="checkbox" data-testid={`applicant-check-${a.id}`} checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
            {s.photo_url ? (
              <img
                src={s.photo_url}
                alt=""
                referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-12 h-12 rounded-full object-cover ring-2 ring-slate-200"
              />
            ) : null}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-slate-900">{s.full_name || "Applicant"}</div>
              <div className="text-xs text-slate-500">{s.email} · {s.phone}</div>
              {s.headline && <div className="text-xs text-slate-600 mt-0.5 italic truncate">{s.headline}</div>}
            </div>
            <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest bg-slate-100 text-slate-700">{a.status}</span>
            {a.portfolio_url && (
              <a
                href={a.portfolio_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`applicant-portfolio-${a.id}`}
                title={a.portfolio_url}
                className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
              >
                Portfolio
              </a>
            )}
            <Link to={`/jobs/applications/${a.id}`} className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline" data-testid={`applicant-open-${a.id}`}>Open</Link>
          </li>
          );
        })}
      </ul>
    </div>
  );
}
